import { isAxiosError } from 'axios';
import createClient from 'openapi-fetch';

import { env } from '@/lib/env';
import { http } from '@/lib/http';
import { apiErrorFrom } from '@/lib/typed-api-error';
import type { paths } from '@/types/generated/openapi';

// 204/205/304 forbid a body on the Fetch Response constructed below.
const NO_BODY_STATUSES = new Set([204, 205, 304]);

// The generated client must issue requests through this same axios instance — not a bare
// `fetch`, openapi-fetch's default — or it silently loses `http.ts`'s 401 refresh-and-retry
// interceptor and cookie auth. This adapter is the seam: it lets an axios call run exactly as
// every other `http.*` call does (interceptors, axios-retry, withCredentials), then reshapes
// the settled response into a Fetch `Response` for openapi-fetch to parse.
const axiosFetch: typeof fetch = async (input, init) => {
  const request = input instanceof Request ? input : new Request(input, init);
  const body = await readBody(request);

  try {
    const response = await http.request({
      url: request.url,
      method: request.method,
      data: body,
      headers: Object.fromEntries(request.headers.entries()),
      signal: request.signal,
      responseType: 'text',
    });
    return toFetchResponse(
      response.status,
      response.data as string,
      response.headers,
    );
  } catch (error) {
    if (isAxiosError(error) && error.response) {
      return toFetchResponse(
        error.response.status,
        error.response.data as string,
        error.response.headers,
      );
    }
    throw error;
  }
};

// A multipart body carries file bytes, and `request.text()` decodes them as UTF-8 — a CSV
// exported as latin-1 comes out as U+FFFD and axios re-encodes the damage, so the backend
// never sees the bytes it was asked to reject. Read those requests as an ArrayBuffer instead;
// the boundary rides in the Content-Type header the caller already forwards. Everything else
// keeps the text path, which is what axios wants for a JSON body.
async function readBody(
  request: Request,
): Promise<string | ArrayBuffer | undefined> {
  if (request.headers.get('content-type')?.startsWith('multipart/form-data')) {
    return request.arrayBuffer();
  }
  const text = await request.text();
  return text === '' ? undefined : text;
}

function toFetchResponse(
  status: number,
  data: string,
  headers: unknown,
): Response {
  return new Response(NO_BODY_STATUSES.has(status) ? null : data, {
    status,
    headers: normalizeHeaders(headers),
  });
}

function normalizeHeaders(headers: unknown): HeadersInit {
  const source =
    headers && typeof (headers as { toJSON?: unknown }).toJSON === 'function'
      ? (headers as { toJSON: () => Record<string, unknown> }).toJSON()
      : ((headers ?? {}) as Record<string, unknown>);
  const plain: Record<string, string> = {};
  for (const [key, value] of Object.entries(source)) {
    if (typeof value === 'string') plain[key] = value;
    else if (Array.isArray(value)) plain[key] = value.join(', ');
  }
  return plain;
}

export const apiClient = createClient<paths>({
  baseUrl: env.VITE_API_BASE_URL,
  fetch: axiosFetch,
});

// Every repository method resolves to `data` or throws — matches the axios-era contract the
// hooks (TanStack Query queryFn/mutationFn) already depend on, so callers need no changes.
//
// `data`/`error` are deliberately untyped here rather than structurally unified with the
// caller's declared T: openapi-fetch infers response shapes from `openapi.d.ts` (openapi-typescript),
// while T is the canonical model type from `@/types/generated` (json2ts) — both trace back to
// the same Pydantic model, but the two generators render a couple of edge cases differently
// (e.g. a `Decimal` field, a fixed-length tuple). Asserting the boundary once here is simpler
// and more robust than fighting the two generators into byte-identical output.
export async function unwrapApiResult<T>(
  result: Promise<{ data?: unknown; error?: unknown; response: Response }>,
): Promise<T> {
  const { data, error, response } = await result;
  // `response.ok`, not `error`: a bodyless 502 parses to an empty string, which is falsy —
  // keying on the body alone resolves that failure as a successful `undefined`.
  if (!response.ok) throw apiErrorFrom(response.status, error);
  return data as T;
}
